import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { api } from '../../../utils/api'
import { reportError } from '../../../utils/reportError'
import { trackUserAction } from '../../../utils/telemetry'
import { normalizeCustomerSummaries } from '../../../search'
import { filterCustomerSuggestionsByField } from './helpers'
import type { CustomerSuggestion } from './types'

export function useCustomerTypeahead() {
  const [customerSuggestions, setCustomerSuggestions] = useState<CustomerSuggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false)
  const [customerSearchError, setCustomerSearchError] = useState<string | null>(null)
  const [activeSuggestion, setActiveSuggestion] = useState(-1)
  const [activeSuggestionField, setActiveSuggestionField] = useState<'name' | 'email' | 'phone' | null>(null)
  const customerNameRef = useRef<HTMLInputElement>(null)
  const customerEmailRef = useRef<HTMLInputElement>(null)
  const customerPhoneRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const customerSearchAbortRef = useRef<AbortController | null>(null)
  const customerSearchSeqRef = useRef(0)

  const searchCustomers = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      customerSearchAbortRef.current?.abort()
      setCustomerSearchLoading(false)
      setCustomerSearchError(null)
      setCustomerSuggestions([])
      setShowSuggestions(false)
      return
    }

    customerSearchAbortRef.current?.abort()
    const ac = new AbortController()
    customerSearchAbortRef.current = ac
    const seq = ++customerSearchSeqRef.current

    setCustomerSearchLoading(true)
    setCustomerSearchError(null)

    try {
      const res = await api.get('/trades/customers/search', {
        params: { q: query.trim(), limit: 8 },
        signal: ac.signal,
      })
      if (seq !== customerSearchSeqRef.current) return
      if (res.data.success && res.data.data.length > 0) {
        setCustomerSuggestions(normalizeCustomerSummaries(res.data.data))
        setShowSuggestions(true)
      } else {
        setCustomerSuggestions([])
        setShowSuggestions(true)
      }
    } catch (err: unknown) {
      if (axios.isCancel(err)) return
      if (seq !== customerSearchSeqRef.current) return
      reportError(err, { page: 'CreateTransaction', action: 'searchCustomers' })
      trackUserAction('customer_search_failed', {
        queryLen: query.trim().length,
        message: 'Could not load suggestions. Check your connection and try again.',
      })
      setCustomerSuggestions([])
      setShowSuggestions(true)
      setCustomerSearchError('Could not load suggestions. Check your connection and try again.')
    } finally {
      if (seq === customerSearchSeqRef.current) {
        setCustomerSearchLoading(false)
      }
    }
  }, [])

  const clearSuggestionUi = useCallback(() => {
    setShowSuggestions(false)
    setActiveSuggestionField(null)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node) &&
        customerNameRef.current && !customerNameRef.current.contains(e.target as Node) &&
        customerEmailRef.current && !customerEmailRef.current.contains(e.target as Node) &&
        customerPhoneRef.current && !customerPhoneRef.current.contains(e.target as Node)
      ) {
        clearSuggestionUi()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [clearSuggestionUi])

  return {
    customerSuggestions,
    setCustomerSuggestions,
    showSuggestions,
    customerSearchLoading,
    customerSearchError,
    activeSuggestion,
    activeSuggestionField,
    setActiveSuggestion,
    setActiveSuggestionField,
    setShowSuggestions,
    setCustomerSearchError,
    setCustomerSearchLoading,
    customerNameRef,
    customerEmailRef,
    customerPhoneRef,
    suggestionsRef,
    debounceTimer,
    searchCustomers,
    clearSuggestionUi,
  }
}

export function useFilteredCustomerSuggestions(
  activeSuggestionField: 'name' | 'email' | 'phone' | null,
  customerName: string,
  customerEmail: string,
  customerPhone: string,
  customerSuggestions: CustomerSuggestion[],
) {
  return useMemo(() => {
    if (!activeSuggestionField || customerSuggestions.length === 0) return customerSuggestions
    const q =
      activeSuggestionField === 'name'
        ? customerName
        : activeSuggestionField === 'email'
          ? customerEmail
          : customerPhone
    return filterCustomerSuggestionsByField(activeSuggestionField, q, customerSuggestions)
  }, [activeSuggestionField, customerName, customerEmail, customerPhone, customerSuggestions])
}
